// @ts-nocheck
/* Starter Sync - BrandDetailsImage */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import LinkBack, { LinkBackPresentationProps } from "@store/Library/Components/Toolkit/LinkBack";
import OrderHeader, { OrderHeaderStyles } from "@store/Library/Components/Toolkit/Order/OrderHeader";
import { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { brandProps } from "@store/Themes/common/props/brand";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useContext } from "react";

const enum fields {
    showLogo = "showLogo",
    showLogoOnly = "showLogoOnly",
}

export interface BrandDetailsTitleProps extends WidgetProps {
    fields: {
        [fields.showLogo]: boolean;
        [fields.showLogoOnly]: boolean;
    };
}

export interface BrandDetailsTitleStyles {
    linkBack?: LinkBackPresentationProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    externalLink?: LinkPresentationProps;
    productLink?: LinkPresentationProps;
    orderHeader?: OrderHeaderStyles;
}

export const brandDetailsTitleStyles: BrandDetailsTitleStyles = {
    imageWrapper: { css: spacingStyles() },
    image: brandProps.logoSmall,
    externalLink: linkGetActionLinkProps(),
    productLink: linkGetActionLinkProps(),
    orderHeader: {
        heading: {
            extendedStyles: {
                image: brandProps.logoSmall,
            },
        },
    },
};

const styles = brandDetailsTitleStyles;

const BrandDetailsTitle: FC<BrandDetailsTitleProps> = ({ fields }) => {
    const { value: brand } = useContext(BrandStateContext);
    const brandListLink = usePageLinkByPageType(constants.pageType.brands);

    if (!brand) {
        return null;
    }

    const showLogo = Boolean(fields.showLogo && brand.logoSmallImagePath);
    const showLogoOnly = Boolean(showLogo && fields.showLogoOnly);
    const logoProps: Pick<ImgProps, "src" | "altText"> = {
        altText: brand.logoAltText || brand.name,
        src: brand.logoSmallImagePath,
    };

    return (
        <>
            <LinkBack pageLink={brandListLink} {...styles.linkBack} />
            {showLogo && !showLogoOnly && (
                <StyledWrapper {...styles.imageWrapper}>
                    <LazyImage {...logoProps} {...styles.image} />
                </StyledWrapper>
            )}
            <OrderHeader
                image={showLogoOnly ? logoProps : undefined}
                title={brand.name}
                extendedStyles={styles.orderHeader}
            >
                {brand.externalUrl && (
                    <Link
                        data-test-selector="brandExternalUrlLink"
                        href={brand.externalUrl}
                        {...styles.externalLink}
                        target="_blank"
                    >
                        {translate("Visit Website")}
                    </Link>
                )}
                <Link
                    data-test-selector="brandToProductPageLink"
                    href={brand.productListPagePath}
                    {...styles.productLink}
                >
                    {translate("Shop All Products")}
                </Link>
            </OrderHeader>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsTitle,
    definition: {
        group: "Brand Details",
        displayName: "Brand Title",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showLogo,
                displayName: "Show Logo",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showLogoOnly,
                displayName: "Only show the logo",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: x => x.fields.showLogo,
            },
        ],
    },
};

export default widgetModule;
